import React from 'react';
import PhotoUpload from '../common/PhotoUpload';
import { EventItem } from '../../types/event';
import { logger } from '../../utils/logger';
import { createEvent, updateEvent } from '../../services/eventService';
import { getTodayDateString, validateEventDate } from '../../utils/dateUtils';
import './EventCreateModal.css';
import LoadingSpinner from '../common/LoadingSpinner';
import ConfirmDialog from '../common/ConfirmDialog';

interface EventCreateModalProps {
  open: boolean;
  form: Omit<EventItem, 'id'>;
  setForm: React.Dispatch<React.SetStateAction<Omit<EventItem, 'id'>>>;
  imageFile: File | null;
  setImageFile: React.Dispatch<React.SetStateAction<File | null>>;
  imagePreview: string | null;
  setImagePreview: React.Dispatch<React.SetStateAction<string | null>>;
  formError: string | null;
  setFormError: React.Dispatch<React.SetStateAction<string | null>>;
  submitting: boolean;
  setSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
  onClose: () => void;
  onSuccess?: (event: EventItem) => void;
  editMode?: boolean;
  eventId?: string;
}

const EventCreateModal: React.FC<EventCreateModalProps> = ({
  open,
  form,
  setForm,
  imageFile,
  setImageFile,
  imagePreview,
  setImagePreview,
  formError,
  setFormError,
  submitting,
  setSubmitting,
  onClose,
  onSuccess,
  editMode = false,
  eventId,
}) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState<boolean>(false);
  const [showConfirmClose, setShowConfirmClose] = React.useState<boolean>(false);
  
  React.useEffect(() => {
    // Check if the form has been modified
    if (form.title || form.date || form.address || form.description || 
        form.startTime.doorsOpen || form.startTime.doorsClose || form.endTime || 
        imageFile || form.imageUrl) {
      setHasUnsavedChanges(true);
    } else {
      setHasUnsavedChanges(false);
    }
  }, [form, imageFile]);

  if (!open) return null;

  const handleClose = () => {
    if (hasUnsavedChanges && !submitting) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  };

  const confirmClose = () => {
    setShowConfirmClose(false);
    onClose();
  };

  const cancelClose = () => {
    setShowConfirmClose(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    
    try {
      // Validate all fields
      if (!form.title || !form.date || !form.address || !form.description || 
          !form.startTime.doorsOpen || !form.startTime.doorsClose || !form.endTime || 
          (!imageFile && !form.imageUrl)) {
        setFormError('All fields are required, including event image.');
        setSubmitting(false);
        return;
      }
      
      if (form.description.length > 1000) {
        setFormError('Description must be 1000 characters or less.');
        setSubmitting(false);
        return;
      }
      
      // Validate event date is not in the past
      const dateValidationError = validateEventDate(form.date);
      if (dateValidationError) {
        setFormError(dateValidationError);
        setSubmitting(false);
        return;
      }
      
      let eventData: EventItem;
      
      if (editMode && eventId) {
        // Update existing event
        logger.info('🔄 Updating event in Firestore...', { id: eventId, title: form.title });
        eventData = await updateEvent(eventId, form, imageFile);
        logger.success('✅ Event updated successfully', { id: eventData.id });
      } else {
        // Create new event
        logger.info('🔄 Creating event in Firestore...', { title: form.title });
        eventData = await createEvent(form, imageFile);
        logger.success('✅ Event created successfully', { id: eventData.id });
      }
      
      // Reset form
      setForm({
        title: '',
        date: '',
        imageUrl: '',
        address: '',
        description: '',
        startTime: {
          doorsOpen: '',
          doorsClose: ''
        },
        endTime: ''
      });
      setImageFile(null);
      setImagePreview(null);
      
      // Call success callback if provided
      if (onSuccess) {
        onSuccess(eventData);
      }
      
      // Close modal
      onClose();
    } catch (err: unknown) {
      logger.error(`❌ Failed to ${editMode ? 'update' : 'create'} event:`, err);
      if (typeof err === 'object' && err && 'message' in err) {
        setFormError((err as any).message);
      } else {
        setFormError(`Failed to ${editMode ? 'update' : 'create'} event`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div
        className="modal-backdrop"
        tabIndex={-1}
        aria-modal="true"
        role="dialog"
        onClick={(e) => {
          e.stopPropagation();
          handleClose();
        }}
      >
        <div
          className="event-create-modal"
          tabIndex={-1}
          onClick={e => e.stopPropagation()}
        >
          <div className="modal-header">
            <h3>{editMode ? 'Edit Event' : 'Create New Event'}</h3>
            <button type="button" className="close-modal-button" onClick={handleClose} aria-label="Close">
              &times;
            </button>
          </div>
          <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title *</label>            <input
              type="text"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              required
              placeholder="Enter event title"
              title="Event title"
            />
          </div>
          <div className="form-group">
            <label>Date *</label>            <input
              type="date"
              value={form.date}
              min={getTodayDateString()}
              onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
              required
              title="Event date (must be today or in the future)"
            />
          </div>
          <div className="form-group">
            <label>Location *</label>            <input
              type="text"
              value={form.address}
              onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
              required
              placeholder="Enter event location address"
              title="Event location address"
            />
          </div>
          <div className="form-group">
            <label>Event Image</label>
            <PhotoUpload
              value={imageFile}
              previewUrl={imagePreview}
              onChange={(file, preview) => {
                setImageFile(file);
                setImagePreview(preview);
                if (file && preview) {
                  logger.info('📸 Image file selected for event');
                }
              }}
              helperText="Upload an image for the event (max 5MB)"
            />
          </div>
          <div className="form-group">
            <label>Description *</label>            <textarea
              value={form.description}
              maxLength={1000}
              required
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Enter event description"
              title="Event description"
            />
            <div className="character-count">{form.description.length}/1000</div>
          </div>
          <div className="form-group time-inputs-container">
            <div className="time-input">
              <label>Doors Open *</label>              <input
                type="time"
                value={form.startTime.doorsOpen}
                required
                onChange={e => setForm(f => ({ ...f, startTime: { ...f.startTime, doorsOpen: e.target.value } }))}
                title="Doors open time"
              />
            </div>
            <div className="time-input">
              <label>Doors Close *</label>              <input
                type="time"
                value={form.startTime.doorsClose}
                required
                onChange={e => setForm(f => ({ ...f, startTime: { ...f.startTime, doorsClose: e.target.value } }))}
                title="Doors close time"
              />
            </div>
            <div className="time-input">
              <label>Event End Time *</label>              
              <input
                type="time"
                value={form.endTime || ''}
                required
                onChange={e => setForm(f => ({ ...f, endTime: e.target.value }))}
                title="Event end time"
              />
            </div>
          </div>
          {formError && <div className="error">{formError}</div>}
          <div className="modal-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button type="submit" className="primary-button" disabled={submitting}>
              {submitting ? (
                <>
                  <LoadingSpinner />
                  {editMode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                editMode ? 'Update' : 'Create'
              )}
            </button>
          </div>        
          </form>
      </div>
    </div>
    
    {showConfirmClose && (
      <ConfirmDialog
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to close without saving?"
        confirmLabel="Close Without Saving"
        cancelLabel="Continue Editing"
        isDestructive={true}
        onConfirm={confirmClose}
        onCancel={cancelClose}
      />
    )}
    </>
  );
};

export default EventCreateModal;
